import { FormEvent, FunctionComponent, useContext, useState } from "react";
import Select from "react-select";
import BackButton from "../components/back-button/BackButton";
import Button from "../components/button/Button";
import FileUploader from "../components/file-uploader/FileUploader";
import Input from "../components/input/Input";
import CustomSelect from "../components/select/Select";
import Table, { Column, Pagination } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import UserContext from "../context/user-context";
import { uploadCSV } from "../utils/helpers/data/upload-convert";
import {
  getOptionsFromArray,
  parseCSV,
} from "../utils/helpers/type-conversions";
import {
  ABUMandateRecord,
  ABUMandateRecord265,
  User,
} from "../utils/helpers/types";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import styles from "./index.module.scss";

const uploadTypes = ["R276", "R625"];

const getInputColumns: (uploadType: UploadType) => Column[] = (uploadType) => {
  return [
    {
      title: "Ica_Number",
      key: "Ica_Number",
      dataIndex: "Ica_Number",
      width: "12rem",
    },
    {
      title: "Merchant_Id",
      key: "Merchant_Id",
      dataIndex: "Merchant_Id",
      width: "15rem",
    },
    {
      title: "Merchant_Name",
      key: "Merchant_Name",
      dataIndex: "Merchant_Name",
      width: "12rem",
    },
    uploadType === "R276" && {
      title: "Old_Acct_Number",
      key: "Old_Acct_Number",
      dataIndex: "Old_Acct_Number",
      width: "15rem",
    },
    uploadType === "R276" && {
      title: "Old_Exp_Date",
      key: "Old_Exp_Date",
      dataIndex: "Old_Exp_Date",
      width: "10rem",
    },
    uploadType === "R276" && {
      title: "Acquirer_Discretionary_Data_1",
      key: "Acquirer_Discretionary_Data_1",
      dataIndex: "Acquirer_Discretionary_Data_1",
      width: "20rem",
    },
    uploadType === "R276" && {
      title: "Acquirer_Discretionary_Data_2",
      key: "Acquirer_Discretionary_Data_2",
      dataIndex: "Acquirer_Discretionary_Data_2",
      width: "20rem",
    },

    uploadType === "R625" && {
      title: "Request_Type",
      key: "Request_Type",
      dataIndex: "Request_Type",
      width: "10rem",
    },
    uploadType === "R625" && {
      title: "Mcc_Code",
      key: "Mcc_Code",
      dataIndex: "Mcc_Code",
      width: "10rem",
    },
  ].filter(BooleanFilter) as Column[];
};

export type UploadType = (typeof uploadTypes)[number];

const HistoryPage: FunctionComponent = () => {
  const [loading, setLoading] = useState(false);
  const [inputFile, setInputFile] = useState<File | null>(null);
  // const [email, setEmail] = useState("");
  const [inputPagination, setInputPagination] = useState<Pagination>({
    pageNumber: 1,
    pageSize: 10,
    total: 0,
  });
  const [uploadType, setUploadType] = useState<UploadType | null>(null);
  const [responsePayload, setResponsePayload] = useState("");
  const [inputDataSource, setInputDataSource] = useState<
    ABUMandateRecord[] | ABUMandateRecord265[]
  >([]);
  // const [selectedOption, setSelectedOption] = useState<any>(null);

  const user = useContext(UserContext);
  const { notify, setGlobalLoading } = useContext(LayoutContext);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!inputFile) {
      notify("error", "Please upload a CSV file to continue");
      return;
    }
    if (!uploadType) {
      notify("error", "Please select an Upload Type to continue");
      return;
    }
    setLoading(true);
    const response = await uploadCSV({
      file: inputFile,
      // email,
      user: user as User,
      type: uploadType,
    });
    setLoading(false);
    if (response.error) {
      notify("error", `Unable to convert CSV: ${response.message}`);
      return;
    }
    setResponsePayload(response.data ?? "");
    notify("success", "CSV conversion is complete!");
  };

  const processData: (
    data: any[]
  ) => ABUMandateRecord[] | ABUMandateRecord265[] = (data) => {
    const output: ABUMandateRecord[] | ABUMandateRecord265[] = [];
    data.forEach((record) => {
      if (Object.keys(record).length > 3) {
        output.push(record);
      }
    });
    if ((output[0] as ABUMandateRecord)?.Old_Exp_Date) {
      setUploadType("R276");
    } else if ((output[0] as ABUMandateRecord265)?.Mcc_Code) {
      setUploadType("R625");
    }
    return output;
  };

  const handleFileUpload = async (file: File | null) => {
    setInputFile(file);
    if (file) {
      setGlobalLoading(true);
      const data = await parseCSV(file);
      const processedData = processData(data);
      setInputDataSource(processedData);
      setInputPagination({
        ...inputPagination,
        pageNumber: 1,
        total: processedData.length,
      });
      setTimeout(() => setGlobalLoading(false), 500);
    }
  };

  const handleClear = () => {
    setInputFile(null);
    // setEmail("");
    setResponsePayload("");
    setInputDataSource([]);
    setInputPagination({
      pageNumber: 1,
      pageSize: 10,
      total: 0,
    });
    setUploadType(null);
    // setSelectedOption(null);
  };

  const handleFileDownload = (downloadType: string) => {
    const href = /276/.test(String(downloadType))
      ? "https://storage.googleapis.com/miscellanous/Interswitch/ABU/abu-sample-upload-R276.csv"
      : "https://storage.googleapis.com/miscellanous/Interswitch/ABU/abu-sample-upload-R625.csv";
    const link = document.createElement("a");
    link.href = href;
    link.download = "abu-sample-upload.csv";
    link.click();
  };

  return (
    <main className="content-layout">
      <h1 className="title vertical-margin spaced thin">
        MC Acquirer ABU Mandate Converter
      </h1>
      {!responsePayload && (
        <form onSubmit={handleSubmit}>
          <div className="input-group half-width">
            <span className="question flex-start">
              <span>Upload File</span>
              <span className="danger margin-left compact">*</span>
            </span>
            <FileUploader
              file={inputFile}
              acceptedTypes={[".csv"]}
              titleText="Upload from your local computer"
              subtitle="Or drag a CSV file here"
              onChange={handleFileUpload}
              onClear={handleClear}
            />
            <div className="flex end margin-top">
              <CustomSelect
                placeholder="Download sample CSV"
                value=""
                onSelect={(value) => handleFileDownload(value as string)}
                options={getOptionsFromArray([
                  "Download R276 format",
                  "Download R625 format",
                ])}
                theme="dark"
                dropdownOnTop
              />
            </div>
          </div>

          <div className="input-group half-width">
            <span className="question flex-start">
              <span>Upload Type</span>
              <span className="danger margin-left compact">*</span>
            </span>
            {/* <Select
              defaultValue={selectedOption}
              onChange={setSelectedOption}
              options={selectOptions}
              placeholder="Select upload type"
              styles={{
                placeholder: (baseStyles, state) => ({
                  ...baseStyles,
                  fontSize: "1rem"
                })
              }}
            /> */}
            <CustomSelect
              theme="light"
              responsive
              value={uploadType ?? ""}
              onSelect={(value) => setUploadType(value as UploadType)}
              options={getOptionsFromArray([...uploadTypes])}
              placeholder="Select Upload Type"
            />
          </div>

          {/* <div className="input-group half-width">
            <span className="question flex-start">
              <span>Notify by email</span>
              <span className="danger margin-left compact">*</span>
            </span>
            <Input
              type="email"
              required
              value={email}
              onChange={setEmail}
              placeholder="Enter email"
              name="email"
              dimmed
            />
          </div> */}

          {inputDataSource.length > 0 && (
            <div className={styles.right}>
              <h3 className="thin">Upload Preview</h3>
              <div className={styles["preview-wrapper"]}>
                <Table
                  columns={getInputColumns(uploadType ?? "R625")}
                  dataSource={inputDataSource}
                  paginateOnBrowser
                  pagination={inputPagination}
                  onPageChange={(pageNumber) =>
                    setInputPagination({ ...inputPagination, pageNumber })
                  }
                  onPageSizeChange={(pageSize) =>
                    setInputPagination({ ...inputPagination, pageSize })
                  }
                />
              </div>
            </div>
          )}

          <div className="flex spaced half-width vertical-margin wide">
            <Button onClick={handleClear} type="accent">
              CLEAR
            </Button>
            <Button buttonType="submit" loading={loading} disabled={loading}>
              SUBMIT
            </Button>
          </div>
        </form>
      )}

      {responsePayload && (
        <div className="flex column vertical-margin">
          <BackButton
            className="vertical-margin"
            round
            text="Go Back"
            onClick={handleClear}
          />
          <br />
          <br />
          <h2 className="thin vertical-margin">
            <span>Your download is ready!</span>
          </h2>
          <a
            href={`data:text/plain;charset=utf-8,${encodeURIComponent(
              responsePayload
            )}`}
            download={`abu-mandate-output-${uploadType}`}
            className={styles["download-link"]}
          >
            Download
          </a>
        </div>
      )}
    </main>
  );
};

export default HistoryPage;
